import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Search, Plus, Minus, Trash2, ShoppingBag, Check, Loader2, AlertTriangle, Store, Package, Receipt, Users, X, Pencil } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { usePOSRetailProducts } from '../../lib/menu-hooks';
import { useTenant } from '../../lib/tenant-context';
import { supabase } from '../../lib/supabase';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency, cn } from '../../lib/utils';
import { PayTableModal } from '../../components/admin/PayTableModal';
import type { TableWithOrder } from '../../lib/types';
interface CartLine {
  id: string;
  menuItemId: string;
  code: string;
  name: string;
  unitPrice: number;
  quantity: number;
  stock?: number | null;
}
interface ActiveTable {
  orderId: string;
  tableNumber: string | number | null;
  customerName: string | null;
  total: number | null;
}
interface ProductForm {
  id: string | null;
  name: string;
  price: string;
  code: string;
  stock: string;
}
const EMPTY_PRODUCT_FORM: ProductForm = {
  id: null,
  name: '',
  price: '',
  code: '',
  stock: ''
};
function slugify(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || `producto-${Date.now()}`;
}
export function POSCajaPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = useTenant();
  const isNatuCafe = String(restaurant?.id) === '19' || restaurant?.slug === 'natucafe-coffee-shop';
  const {
    items,
    loading: itemsLoading,
    refetch
  } = usePOSRetailProducts(restaurant?.id ? String(restaurant.id) : undefined);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  // Active-table picker
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [activeTables, setActiveTables] = useState<ActiveTable[]>([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  // ─── Pay modal (Venta rápida con factura/efectivo/tarjeta/SINPE) ──
  // En vez de insertar la orden ya cobrada (lo que rompía contra
  // orders_status_check), creamos la orden en status='pending' y
  // abrimos PayTableModal — el mismo modal de cerrar mesa, con
  // selección de método de pago + opción de factura. processPayment
  // se encarga de mover la orden a status='delivered' (canónico) y
  // de disparar el print job de factura para NatuCafé.
  const [payTable, setPayTable] = useState<TableWithOrder | null>(null);
  const [showPayModal, setShowPayModal] = useState(false);
  // ─── Product CRUD (Estantería) ────────────────────────────
  const [showProductForm, setShowProductForm] = useState(false);
  const [productForm, setProductForm] = useState<ProductForm>(EMPTY_PRODUCT_FORM);
  const [savingProduct, setSavingProduct] = useState(false);
  const [productError, setProductError] = useState<string | null>(null);
  // ─── Search filter ────────────────────────────────────────
  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it: any) => {
      const name = (it.name || '').toLowerCase();
      const code = String(it.external_pos_id ?? '').toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [items, search]);
  // ─── Cart helpers ─────────────────────────────────────────
  const addToCart = (item: any) => {
    setError(null);
    setSuccessMsg(null);
    setCart(prev => {
      const existing = prev.find(l => l.menuItemId === String(item.id));
      if (existing) {
        return prev.map(l => l.id === existing.id ? {
          ...l,
          quantity: l.quantity + 1
        } : l);
      }
      return [...prev, {
        id: `${item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        menuItemId: String(item.id),
        code: String(item.external_pos_id ?? ''),
        name: item.name,
        unitPrice: Number(item.price) || 0,
        quantity: 1,
        stock: item.stock_quantity === null || item.stock_quantity === undefined ? null : Number(item.stock_quantity)
      }];
    });
  };
  const updateQty = (lineId: string, delta: number) => {
    setCart(prev => prev.map(l => l.id === lineId ? {
      ...l,
      quantity: Math.max(0, l.quantity + delta)
    } : l).filter(l => l.quantity > 0));
  };
  const removeLine = (lineId: string) => {
    setCart(prev => prev.filter(l => l.id !== lineId));
  };
  const cartTotal = useMemo(() => cart.reduce((s, l) => s + l.unitPrice * l.quantity, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, l) => s + l.quantity, 0), [cart]);
  // ─── Quick sale: create a new order ───────────────────────
  const handleQuickSale = async () => {
    if (!restaurant?.id) return;
    if (cart.length === 0) {
      setError('Agrega al menos un producto al carrito.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const subtotal = cartTotal;
      const trackingToken = typeof crypto !== 'undefined' && (crypto as any).randomUUID ? (crypto as any).randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      // Crear la orden en status='pending' (valor canónico del
      // check constraint orders_status_check). El cobro se hace
      // adentro de PayTableModal — ahí processPayment la mueve a
      // 'delivered' al insertar el payment. source='pos_quick_sale'
      // marca la venta de mostrador (el trigger de print de cocina
      // ignora estas órdenes; sólo se imprime la factura al cobrar).
      const {
        data: orderRow,
        error: orderErr
      } = await supabase.from('orders').insert({
        restaurant_id: Number(restaurant.id),
        tracking_token: trackingToken,
        customer_name: 'Estantería',
        customer_phone: null,
        delivery_type: 'dine_in',
        payment_method: 'cash',
        status: 'pending',
        subtotal,
        delivery_fee: 0,
        total: subtotal,
        notes: 'Venta rápida Estantería',
        source: 'pos_quick_sale'
      }).select('id, order_number, tracking_token, restaurant_id, customer_name, customer_phone, delivery_type, payment_method, table_id, status, subtotal, delivery_fee, total, notes, source, created_at, updated_at').single();
      if (orderErr) throw orderErr;
      if (!orderRow?.id) throw new Error('No se pudo crear la orden.');
      // Insert order_items en 'pending' — el trigger de print de
      // cocina los ignora porque la orden tiene source='pos_quick_sale'
      // (guard en SETUP_NATUCAFE_PRINT_ON_ITEMS_TRIGGER.sql).
      const itemsPayload = cart.map(l => ({
        order_id: orderRow.id,
        restaurant_id: Number(restaurant.id),
        menu_item_id: l.menuItemId,
        item_name_snapshot: l.name,
        unit_price_snapshot: l.unitPrice,
        quantity: l.quantity,
        line_total: l.unitPrice * l.quantity,
        status: 'pending',
        notes: l.code ? `Cód. ${l.code}` : null
      }));
      const {
        error: itemsErr
      } = await supabase.from('order_items').insert(itemsPayload);
      if (itemsErr) throw itemsErr;
      // Construir TableWithOrder sintético — PayTableModal acepta
      // este shape (mismo patrón que CajaOverviewPage para órdenes
      // marketplace sin mesa real). table.id=null hace que el modal
      // cargue items vía orderId fallback.
      const syntheticTable: TableWithOrder = {
        id: '' as any,
        restaurant_id: String(restaurant.id),
        table_number: 'Estantería' as any,
        table_name: 'Estantería',
        zone: null,
        capacity: null,
        qr_code_url: null,
        qr_token: null,
        is_active: true,
        created_at: '',
        updated_at: '',
        current_order: {
          id: String(orderRow.id),
          restaurant_id: String(restaurant.id),
          order_number: String(orderRow.order_number || ''),
          tracking_token: orderRow.tracking_token,
          customer_name: orderRow.customer_name || 'Estantería',
          customer_phone: '',
          delivery_type: 'dine_in' as any,
          payment_method: 'cash',
          table_id: null,
          delivery_address: null,
          delivery_lat: null,
          delivery_lng: null,
          maps_url: null,
          subtotal: Number(orderRow.subtotal) || 0,
          delivery_fee: Number(orderRow.delivery_fee) || 0,
          total: Number(orderRow.total) || 0,
          status: 'pending' as any,
          notes: orderRow.notes,
          source: 'pos_quick_sale',
          ticket_image_url: null,
          created_at: orderRow.created_at || new Date().toISOString(),
          updated_at: orderRow.updated_at || new Date().toISOString()
        },
        current_order_items: [],
        status: 'occupied',
        order_channel: 'cashier',
        time_open_minutes: 0,
        item_count: cart.length
      } as TableWithOrder;
      setPayTable(syntheticTable);
      setShowPayModal(true);
    } catch (e: any) {
      console.error('[POS/Caja] quick sale error:', e);
      setError(e?.message || 'No se pudo registrar la venta.');
    } finally {
      setSubmitting(false);
    }
  };
  // ─── PayTableModal callback ───────────────────────────────
  const handlePaySuccess = (_allPaid: boolean) => {
    // El modal cierra solo cuando el usuario lo cierra; acá sólo
    // limpiamos el carrito y refrescamos productos (stock).
    setCart([]);
    setSuccessMsg('Venta registrada correctamente.');
    refetch();
  };
  const handlePayClose = async () => {
    // pos_quick_sale sin payments = intento abandonado, no pedido abierto real.
    //
    // Al tocar "Venta rápida" creamos la orden de inmediato (status='pending')
    // y abrimos PayTableModal. Si el cajero CIERRA el modal sin registrar
    // ningún pago, esa orden quedaría huérfana (pending, total_pagado=0) e
    // inflaría los contadores de "pedidos de mesa" del Dashboard para siempre.
    //
    // Regla: si la orden NO tiene ningún payment → la cancelamos
    // automáticamente con motivo "Venta rápida abandonada". Si SÍ tiene pagos
    // (parcial o total) → es una venta real y NO se toca.
    const orderId = payTable?.current_order?.id;
    setShowPayModal(false);
    setPayTable(null);
    if (!orderId || !restaurant?.id) return;
    try {
      const {
        count,
        error: payCountErr
      } = await supabase.from('payments').select('id', {
        count: 'exact',
        head: true
      }).eq('order_id', orderId);
      if (payCountErr) throw payCountErr;
      // Venta real con al menos un pago → respetar, no cancelar.
      if ((count ?? 0) > 0) return;
      // Orden huérfana sin pago → cancelar la orden y sus líneas.
      await supabase.from('orders').update({
        status: 'cancelled',
        notes: 'Venta rápida abandonada',
        updated_at: new Date().toISOString()
      }).eq('id', orderId);
      await supabase.from('order_items').update({
        status: 'cancelled'
      }).eq('order_id', orderId);
    } catch (e: any) {
      console.error('[POS/Caja] auto-cancel venta rápida abandonada error:', e);
    }
  };
  // ─── Active table picker ──────────────────────────────────
  const openTablePicker = async () => {
    if (!restaurant?.id) return;
    if (cart.length === 0) {
      setError('Agrega al menos un producto antes de elegir una mesa.');
      return;
    }
    setError(null);
    setSuccessMsg(null);
    setShowTablePicker(true);
    setTablesLoading(true);
    try {
      const {
        data,
        error: e
      } = await supabase.from('orders').select('id, order_number, table_number, customer_name, total, status').eq('restaurant_id', Number(restaurant.id)).eq('delivery_type', 'dine_in').not('table_number', 'is', null).not('status', 'in', '(cancelled,completed,paid,refunded)').order('created_at', {
        ascending: false
      });
      if (e) throw e;
      setActiveTables((data || []).map((r: any) => ({
        orderId: String(r.id),
        tableNumber: r.table_number,
        customerName: r.customer_name,
        total: r.total
      })));
    } catch (e: any) {
      console.error('[POS/Caja] active tables error:', e);
      setError(e?.message || 'No se pudieron cargar las mesas activas.');
      setActiveTables([]);
    } finally {
      setTablesLoading(false);
    }
  };
  const handleAddToTable = async (targetOrderId: string) => {
    if (!restaurant?.id) return;
    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);
    try {
      // Insert order_items
      const itemsPayload = cart.map(l => ({
        order_id: targetOrderId,
        restaurant_id: Number(restaurant.id),
        menu_item_id: l.menuItemId,
        item_name_snapshot: l.name,
        unit_price_snapshot: l.unitPrice,
        quantity: l.quantity,
        line_total: l.unitPrice * l.quantity,
        status: 'pending',
        notes: l.code ? `Cód. ${l.code}` : null
      }));
      const {
        error: itemsErr
      } = await supabase.from('order_items').insert(itemsPayload);
      if (itemsErr) throw itemsErr;
      // Recompute order totals
      const {
        data: allItems,
        error: fetchErr
      } = await supabase.from('order_items').select('line_total, status').eq('order_id', targetOrderId);
      if (fetchErr) throw fetchErr;
      const newSubtotal = (allItems || []).filter((it: any) => it.status !== 'cancelled').reduce((s: number, it: any) => s + (Number(it.line_total) || 0), 0);
      const {
        data: orderRow
      } = await supabase.from('orders').select('delivery_fee').eq('id', targetOrderId).maybeSingle();
      const deliveryFee = Number(orderRow?.delivery_fee) || 0;
      const {
        error: updErr
      } = await supabase.from('orders').update({
        subtotal: newSubtotal,
        total: newSubtotal + deliveryFee,
        updated_at: new Date().toISOString()
      }).eq('id', targetOrderId);
      if (updErr) throw updErr;
      setSuccessMsg('Productos agregados a la mesa.');
      setCart([]);
      setShowTablePicker(false);
      refetch();
    } catch (e: any) {
      console.error('[POS/Caja] add to table error:', e);
      setError(e?.message || 'No se pudieron agregar los productos a la mesa.');
    } finally {
      setSubmitting(false);
    }
  };
  // ─── Product CRUD handlers ────────────────────────────────
  const openNewProduct = () => {
    setProductError(null);
    setProductForm(EMPTY_PRODUCT_FORM);
    setShowProductForm(true);
  };
  const openEditProduct = (item: any) => {
    setProductError(null);
    setProductForm({
      id: String(item.id),
      name: item.name || '',
      price: item.price === null || item.price === undefined ? '' : String(item.price),
      code: item.external_pos_id ? String(item.external_pos_id) : '',
      stock: item.stock_quantity === null || item.stock_quantity === undefined ? '' : String(item.stock_quantity)
    });
    setShowProductForm(true);
  };
  const resolveRetailCategoryId = async (): Promise<string | null> => {
    if (!restaurant?.id) return null;
    // 1) Prefer the category an existing retail product already uses.
    const existingWithCat = items.find((it: any) => it.category_id);
    if (existingWithCat) return String((existingWithCat as any).category_id);
    // 2) Otherwise find the dedicated "Tienda / Productos de caja" category.
    const {
      data
    } = await supabase.from('menu_categories').select('id, name').eq('restaurant_id', Number(restaurant.id)).ilike('name', '%tienda%').limit(1);
    if (data && data.length > 0) return String(data[0].id);
    // 3) Fallback: any active category for this restaurant.
    const {
      data: anyCat
    } = await supabase.from('menu_categories').select('id').eq('restaurant_id', Number(restaurant.id)).order('sort_order', {
      ascending: true
    }).limit(1);
    return anyCat && anyCat.length > 0 ? String(anyCat[0].id) : null;
  };
  const handleSaveProduct = async () => {
    if (!restaurant?.id) return;
    const name = productForm.name.trim();
    const priceNum = Number(productForm.price);
    const stockRaw = productForm.stock.trim();
    const stockNum = stockRaw === '' ? null : Number(stockRaw);
    const code = productForm.code.trim() || null;
    if (!name) {
      setProductError('El nombre es obligatorio.');
      return;
    }
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      setProductError('Ingresa un precio válido.');
      return;
    }
    if (stockNum !== null && (!Number.isFinite(stockNum) || stockNum < 0)) {
      setProductError('El stock debe ser un número válido.');
      return;
    }
    setSavingProduct(true);
    setProductError(null);
    try {
      if (productForm.id) {
        // ── EDIT existing retail product ──
        const {
          error: updErr
        } = await supabase.from('menu_items').update({
          name,
          slug: slugify(name),
          price: priceNum,
          external_pos_id: code,
          stock_quantity: stockNum,
          updated_at: new Date().toISOString()
        }).eq('id', productForm.id);
        if (updErr) throw updErr;
        setSuccessMsg('Producto actualizado.');
      } else {
        // ── ADD new retail product ──
        const categoryId = await resolveRetailCategoryId();
        if (!categoryId) {
          throw new Error('No se encontró una categoría destino. Crea la categoría "Tienda" primero.');
        }
        const {
          error: insErr
        } = await supabase.from('menu_items').insert({
          restaurant_id: Number(restaurant.id),
          category_id: categoryId,
          name,
          slug: slugify(name),
          price: priceNum,
          external_pos_id: code,
          stock_quantity: stockNum,
          product_type: 'retail',
          show_in_pos: true,
          show_in_digital_menu: false,
          is_active: true,
          is_customizable: false,
          sort_order: 9999
        });
        if (insErr) throw insErr;
        setSuccessMsg('Producto agregado.');
      }
      setShowProductForm(false);
      setProductForm(EMPTY_PRODUCT_FORM);
      refetch();
    } catch (e: any) {
      console.error('[POS/Caja] save product error:', e);
      setProductError(e?.message || 'No se pudo guardar el producto.');
    } finally {
      setSavingProduct(false);
    }
  };
  const handleDeleteProduct = async (item: any) => {
    if (!restaurant?.id) return;
    const ok = window.confirm(`¿Borrar "${item.name}" de la Estantería?\n\nEsto lo quita de la venta rápida. El historial de ventas no se afecta.`);
    if (!ok) return;
    setError(null);
    setSuccessMsg(null);
    try {
      // Soft-delete: marca inactivo para no romper el historial de
      // order_items que apunta a este producto (FK). El hook filtra
      // por is_active, así que desaparece de la Estantería al instante.
      const {
        error: delErr
      } = await supabase.from('menu_items').update({
        is_active: false,
        updated_at: new Date().toISOString()
      }).eq('id', item.id);
      if (delErr) throw delErr;
      setSuccessMsg('Producto borrado.');
      refetch();
    } catch (e: any) {
      console.error('[POS/Caja] delete product error:', e);
      setError(e?.message || 'No se pudo borrar el producto.');
    }
  };
  // ─── Loading / gating ─────────────────────────────────────
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-5567">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-5568" />
      </div>;
  }
  if (!isNatuCafe) {
    return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-5569">
        <div className="p-6 flex flex-col items-center justify-center min-h-[50vh] text-center" data-id="element-5570">
          <Store className="w-16 h-16 text-gray-300 mb-4" data-id="element-5571" />
          <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-5572">
            Módulo no disponible
          </h2>
          <p className="text-gray-500" data-id="element-5573">
            Este módulo es exclusivo para NatuCafé.
          </p>
        </div>
      </AdminLayout>;
  }
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-5574">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4" data-id="element-5575">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-5576">
          <div data-id="element-5577">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5578">Estantería</h1>
            <p className="text-sm text-gray-500" data-id="element-5579">
              Venta rápida de productos · {items.length} disponibles
            </p>
          </div>
          <button onClick={openNewProduct} className="h-11 px-4 rounded-xl font-bold text-sm bg-gray-900 hover:bg-gray-800 text-white active:scale-95 transition-all flex items-center justify-center gap-2 shrink-0" data-id="element-5580">
            <Plus className="w-4 h-4" data-id="element-5581" />
            Agregar producto
          </button>
        </div>

        {/* Banners */}
        {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2" data-id="element-5582">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-5583" />
            <span data-id="element-5584">{error}</span>
          </div>}
        {successMsg && <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 flex items-start gap-2" data-id="element-5585">
            <Check className="w-4 h-4 shrink-0 mt-0.5" data-id="element-5586" />
            <span data-id="element-5587">{successMsg}</span>
          </div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-5588">
          {/* LEFT: products */}
          <div className="lg:col-span-2 space-y-3" data-id="element-5589">
            {/* Big search */}
            <div className="relative" data-id="element-5590">
              <Search className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-5591" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por código o nombre… (ej: 1001, miel, café)" autoFocus className="w-full pl-12 pr-4 h-14 rounded-2xl border-2 border-gray-200 text-base font-medium focus:outline-none focus:border-amber-500 bg-white shadow-sm" data-id="element-5592" />
              {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400" aria-label="Limpiar búsqueda" data-id="element-5593">
                  <X className="w-4 h-4" data-id="element-5594" />
                </button>}
            </div>

            {/* Product grid */}
            <div className="bg-white rounded-2xl border border-gray-200 p-3 min-h-[300px]" data-id="element-5595">
              {itemsLoading ? <div className="flex justify-center py-16 text-gray-400" data-id="element-5596">
                  <Loader2 className="w-6 h-6 animate-spin" data-id="element-5597" />
                </div> : visibleItems.length === 0 ? <div className="text-center py-16 text-gray-500" data-id="element-5598">
                  <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" data-id="element-5599" />
                  <p className="text-sm font-medium" data-id="element-5600">
                    {search ? 'Sin resultados para esa búsqueda.' : 'No hay productos de Estantería cargados.'}
                  </p>
                </div> : <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-5601">
                  {visibleItems.map((item: any) => {
                const stock = item.stock_quantity === null || item.stock_quantity === undefined ? null : Number(item.stock_quantity);
                const lowStock = stock !== null && stock <= 0;
                return <div key={item.id} className="p-3 rounded-xl border border-gray-200 flex flex-col gap-2 bg-white" data-id="element-5602">
                        <div className="flex items-start justify-between gap-2" data-id="element-5603">
                          <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded tabular-nums" data-id="element-5604">
                            {item.external_pos_id || '—'}
                          </span>
                          {stock !== null && <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded tabular-nums', lowStock ? 'text-red-700 bg-red-50' : 'text-emerald-700 bg-emerald-50')} data-id="element-5605">
                              Stock {stock}
                            </span>}
                        </div>
                        <p className="font-bold text-sm text-gray-900 leading-snug line-clamp-2 min-h-[2.5rem]" data-id="element-5606">
                          {item.name}
                        </p>
                        <p className="text-base font-black text-gray-900 tabular-nums" data-id="element-5607">
                          {formatCurrency(item.price)}
                        </p>
                        <div className="mt-auto flex items-center gap-1.5" data-id="element-5608">
                          <button onClick={() => addToCart(item)} className="flex-1 h-9 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm flex items-center justify-center gap-1 active:scale-95 transition-all" data-id="element-5609">
                            <Plus className="w-4 h-4" data-id="element-5610" />
                            Agregar
                          </button>
                          <button onClick={() => openEditProduct(item)} className="w-9 h-9 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 flex items-center justify-center active:scale-95 transition-all shrink-0" aria-label={`Editar ${item.name}`} data-id="element-5611">
                            <Pencil className="w-4 h-4" data-id="element-5612" />
                          </button>
                          <button onClick={() => handleDeleteProduct(item)} className="w-9 h-9 rounded-lg border border-gray-200 hover:bg-red-50 hover:border-red-200 text-red-500 flex items-center justify-center active:scale-95 transition-all shrink-0" aria-label={`Borrar ${item.name}`} data-id="element-5613">
                            <Trash2 className="w-4 h-4" data-id="element-5614" />
                          </button>
                        </div>
                      </div>;
              })}
                </div>}
            </div>
          </div>

          {/* RIGHT: cart */}
          <div className="bg-white rounded-2xl border border-gray-200 flex flex-col max-h-[calc(100vh-180px)] sticky top-4" data-id="element-5615">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between" data-id="element-5616">
              <div className="flex items-center gap-2" data-id="element-5617">
                <ShoppingBag className="w-4 h-4 text-gray-700" data-id="element-5618" />
                <p className="font-bold text-sm text-gray-900" data-id="element-5619">
                  Carrito ({cartCount})
                </p>
              </div>
              {cart.length > 0 && <button onClick={() => setCart([])} className="text-xs font-bold text-red-600 hover:text-red-700" data-id="element-5620">
                  Vaciar
                </button>}
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2" data-id="element-5621">
              {cart.length === 0 ? <div className="text-center py-12 text-gray-400 text-sm" data-id="element-5622">
                  Selecciona productos para iniciar una venta.
                </div> : cart.map(l => <div key={l.id} className="rounded-lg border border-gray-200 px-3 py-2" data-id="element-5623">
                    <div className="flex items-start justify-between gap-2 mb-1.5" data-id="element-5624">
                      <div className="min-w-0" data-id="element-5625">
                        {l.code && <span className="text-[10px] font-bold text-amber-700 tabular-nums" data-id="element-5626">
                            {l.code}
                          </span>}
                        <p className="text-sm font-bold text-gray-900 leading-tight truncate" data-id="element-5627">
                          {l.name}
                        </p>
                      </div>
                      <button onClick={() => removeLine(l.id)} className="text-red-500 hover:bg-red-50 w-7 h-7 rounded-md flex items-center justify-center shrink-0" aria-label="Quitar" data-id="element-5628">
                        <Trash2 className="w-3.5 h-3.5" data-id="element-5629" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-5630">
                      <div className="flex items-center gap-1" data-id="element-5631">
                        <button onClick={() => updateQty(l.id, -1)} className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700" data-id="element-5632">
                          <Minus className="w-3.5 h-3.5" data-id="element-5633" />
                        </button>
                        <span className="w-7 text-center font-black text-sm text-gray-900 tabular-nums" data-id="element-5634">
                          {l.quantity}
                        </span>
                        <button onClick={() => updateQty(l.id, +1)} className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700" data-id="element-5635">
                          <Plus className="w-3.5 h-3.5" data-id="element-5636" />
                        </button>
                      </div>
                      <span className="text-sm font-black text-gray-900 tabular-nums" data-id="element-5637">
                        {formatCurrency(l.unitPrice * l.quantity)}
                      </span>
                    </div>
                  </div>)}
            </div>

            <div className="border-t border-gray-100 p-4 space-y-3 bg-gray-50 rounded-b-2xl" data-id="element-5638">
              <div className="flex items-center justify-between" data-id="element-5639">
                <span className="text-sm font-bold text-gray-600" data-id="element-5640">Total</span>
                <span className="text-2xl font-black text-gray-900 tabular-nums" data-id="element-5641">
                  {formatCurrency(cartTotal)}
                </span>
              </div>
              <button onClick={handleQuickSale} disabled={submitting || cart.length === 0} className="w-full h-12 rounded-xl font-black text-sm bg-amber-500 hover:bg-amber-600 text-white active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-5642">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-5643" /> : <>
                    <Receipt className="w-5 h-5" data-id="element-5644" />
                    Venta rápida
                  </>}
              </button>
              <button onClick={openTablePicker} disabled={submitting || cart.length === 0} className="w-full h-11 rounded-xl font-bold text-sm bg-white border-2 border-gray-200 hover:border-gray-300 text-gray-900 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-5645">
                <Users className="w-4 h-4" data-id="element-5646" />
                Agregar a mesa activa
              </button>
            </div>
          </div>
        </div>

        {/* Active table picker modal */}
        {showTablePicker && <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => !submitting && setShowTablePicker(false)} data-id="element-5647">
            <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-5648">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-5649">
                <div data-id="element-5650">
                  <h3 className="font-black text-gray-900" data-id="element-5651">Mesas activas</h3>
                  <p className="text-xs text-gray-500" data-id="element-5652">
                    Selecciona una mesa para sumar los productos.
                  </p>
                </div>
                <button onClick={() => setShowTablePicker(false)} disabled={submitting} className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500" data-id="element-5653">
                  <X className="w-5 h-5" data-id="element-5654" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3" data-id="element-5655">
                {tablesLoading ? <div className="flex justify-center py-12 text-gray-400" data-id="element-5656">
                    <Loader2 className="w-6 h-6 animate-spin" data-id="element-5657" />
                  </div> : activeTables.length === 0 ? <div className="text-center py-12 text-gray-500 text-sm" data-id="element-5658">
                    No hay mesas con orden abierta.
                  </div> : <div className="space-y-2" data-id="element-5659">
                    {activeTables.map(t => <button key={t.orderId} onClick={() => handleAddToTable(t.orderId)} disabled={submitting} className="w-full text-left p-3 rounded-xl border border-gray-200 hover:border-amber-400 hover:bg-amber-50/40 active:scale-[0.99] transition-all flex items-center justify-between gap-3 disabled:opacity-40" data-id="element-5660">
                        <div className="min-w-0" data-id="element-5661">
                          <p className="font-bold text-sm text-gray-900" data-id="element-5662">
                            Mesa {t.tableNumber ?? '—'}
                          </p>
                          {t.customerName && <p className="text-xs text-gray-500 truncate" data-id="element-5663">
                              {t.customerName}
                            </p>}
                        </div>
                        {t.total !== null && <span className="text-sm font-black text-gray-900 tabular-nums shrink-0" data-id="element-5664">
                            {formatCurrency(Number(t.total) || 0)}
                          </span>}
                      </button>)}
                  </div>}
              </div>
            </div>
          </div>}

        {/* Pay modal — Venta rápida con factura/método de pago */}
        <PayTableModal isOpen={showPayModal} onClose={handlePayClose} table={payTable} restaurantId={restaurant?.id} onSuccess={handlePaySuccess} data-id="element-5665" />

        {/* Product add/edit modal */}
        {showProductForm && <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => !savingProduct && setShowProductForm(false)} data-id="element-5666">
            <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-5667">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-5668">
                <div data-id="element-5669">
                  <h3 className="font-black text-gray-900" data-id="element-5670">
                    {productForm.id ? 'Editar producto' : 'Agregar producto'}
                  </h3>
                  <p className="text-xs text-gray-500" data-id="element-5671">Estantería · NatuCafé</p>
                </div>
                <button onClick={() => setShowProductForm(false)} disabled={savingProduct} className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500" aria-label="Cerrar" data-id="element-5672">
                  <X className="w-5 h-5" data-id="element-5673" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4" data-id="element-5674">
                {productError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2" data-id="element-5675">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-5676" />
                    <span data-id="element-5677">{productError}</span>
                  </div>}
                <div data-id="element-5678">
                  <label className="block text-xs font-bold text-gray-700 mb-1" data-id="element-5679">
                    Nombre <span className="text-red-500" data-id="element-5680">*</span>
                  </label>
                  <input type="text" value={productForm.name} onChange={e => setProductForm(f => ({
                ...f,
                name: e.target.value
              }))} placeholder="Ej: Café molido 500g" autoFocus className="w-full h-11 px-3 rounded-lg border border-gray-300 focus:outline-none focus:border-amber-500 text-sm" data-id="element-5681" />
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-5682">
                  <div data-id="element-5683">
                    <label className="block text-xs font-bold text-gray-700 mb-1" data-id="element-5684">
                      Precio <span className="text-red-500" data-id="element-5685">*</span>
                    </label>
                    <input type="number" inputMode="decimal" min={0} value={productForm.price} onChange={e => setProductForm(f => ({
                  ...f,
                  price: e.target.value
                }))} placeholder="0" className="w-full h-11 px-3 rounded-lg border border-gray-300 focus:outline-none focus:border-amber-500 text-sm tabular-nums" data-id="element-5686" />
                  </div>
                  <div data-id="element-5687">
                    <label className="block text-xs font-bold text-gray-700 mb-1" data-id="element-5688">
                      Stock
                    </label>
                    <input type="number" inputMode="numeric" min={0} value={productForm.stock} onChange={e => setProductForm(f => ({
                  ...f,
                  stock: e.target.value
                }))} placeholder="Opcional" className="w-full h-11 px-3 rounded-lg border border-gray-300 focus:outline-none focus:border-amber-500 text-sm tabular-nums" data-id="element-5689" />
                  </div>
                </div>
                <div data-id="element-5690">
                  <label className="block text-xs font-bold text-gray-700 mb-1" data-id="element-5691">
                    Código
                  </label>
                  <input type="text" value={productForm.code} onChange={e => setProductForm(f => ({
                ...f,
                code: e.target.value
              }))} placeholder="Ej: 1001 (opcional)" className="w-full h-11 px-3 rounded-lg border border-gray-300 focus:outline-none focus:border-amber-500 text-sm tabular-nums" data-id="element-5692" />
                  <p className="text-[11px] text-gray-400 mt-1" data-id="element-5693">
                    Código interno para buscar el producto en caja.
                  </p>
                </div>
              </div>
              <div className="border-t border-gray-100 p-4 flex items-center gap-2 bg-gray-50" data-id="element-5694">
                <button onClick={() => setShowProductForm(false)} disabled={savingProduct} className="flex-1 h-11 rounded-xl font-bold text-sm bg-white border-2 border-gray-200 hover:border-gray-300 text-gray-700 active:scale-95 transition-all disabled:opacity-40" data-id="element-5695">
                  Cancelar
                </button>
                <button onClick={handleSaveProduct} disabled={savingProduct} className="flex-1 h-11 rounded-xl font-black text-sm bg-amber-500 hover:bg-amber-600 text-white active:scale-95 transition-all disabled:opacity-40 flex items-center justify-center gap-2" data-id="element-5696">
                  {savingProduct ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-5697" /> : <>
                      <Check className="w-4 h-4" data-id="element-5698" />
                      {productForm.id ? 'Guardar' : 'Agregar'}
                    </>}
                </button>
              </div>
            </div>
          </div>}
      </div>
    </AdminLayout>;
}